/**
 * Projects Preview Block - JS
 * Handles tab switching, AJAX category loading, and animations
 *
 * Features:
 * - Single IntersectionObserver instance for efficiency
 * - Debounced tab clicks to prevent multiple AJAX requests
 * - Proper cleanup of observers and event listeners
 * - Proper validation of global data
 *
 * @package Atlassport
 */

const ProjectsPreview = {
	initialized: false,
	activeObserver: null,
	tabClickTimeout: null,
	isMobile: false,
	observerOptions: {
		threshold: 0.1,
		rootMargin: '0px 0px -100px 0px',
	},

	init: function () {
		// Prevent double initialization
		if (this.initialized) return;
		this.initialized = true;

		// Check if required element exists
		const blockWrapper = document.querySelector('.block-projects-preview');
		if (!blockWrapper) return;

		// Detect mobile for touch events
		this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

		// Initialize features
		this.initTabs();
		this.initScrollAnimations();
	},

	/**
	 * Initialize tab switching with AJAX and debouncing
	 */
	initTabs: function () {
		const self = this;
		const blockWrapper = document.querySelector('.block-projects-preview');
		if (!blockWrapper) return;

		const tabButtons = blockWrapper.querySelectorAll('.block-projects-preview__tabs .nav-link');
		if (tabButtons.length === 0) return;

		tabButtons.forEach((button) => {
			button.addEventListener('click', function (e) {
				// If it's an anchor tag WITHOUT data-bs-target (external link), allow navigation
				if (this.tagName === 'A' && !this.getAttribute('data-bs-target')) {
					return; // Don't prevent default, let it navigate
				}

				e.preventDefault();

				const clickedButton = this;
				const categoryId = clickedButton.dataset.categoryId;
				const targetSelector = clickedButton.getAttribute('data-bs-target');
				const targetPane = blockWrapper.querySelector(targetSelector);

				if (!targetPane) return;

				const grid = targetPane.querySelector('.block-projects-preview__grid');
				if (!grid) return;

				// Add active class to this button
				tabButtons.forEach(btn => btn.classList.remove('active'));
				clickedButton.classList.add('active');

				// Show target pane
				const tabContent = blockWrapper.querySelector('.tab-content');
				const allPanes = tabContent.querySelectorAll('.tab-pane');
				allPanes.forEach(pane => {
					pane.classList.remove('show', 'active');
					pane.style.opacity = '0';
				});

				// Show target pane with animation
				targetPane.classList.add('show', 'active');
				targetPane.style.transition = 'opacity 0.3s ease-in-out';
				targetPane.style.opacity = '1';

				// Trigger animations for items already in the DOM (server-rendered)
				const existingItems = grid.querySelectorAll('.block-projects-preview__item');
				if (existingItems.length > 0) {
					self.animateItems(existingItems);
				}

				// Load projects via AJAX if category is set
				if (categoryId) {
					self.loadCategoryProjects(categoryId, grid);
				}
			});
		});
	},

	/**
	 * Load projects for a category via AJAX
	 */
	loadCategoryProjects: function (categoryId, gridElement) {
		const self = this;

		if (!gridElement) return;

		// Validate global data exists
		if (typeof atlasData === 'undefined' || !atlasData.ajaxUrl) {
			console.error('ProjectsPreview: atlasData not available');
			gridElement.innerHTML = '<p>Error: Configuration missing</p>';
			return;
		}

		gridElement.dataset.loading = 'true';

		// Show loading state
		gridElement.innerHTML = `
			<div class="projects-loading">
				<div class="loader">
					<div class="loader-circle"></div>
					<div class="loader-circle"></div>
					<div class="loader-circle"></div>
				</div>
			</div>
		`;

		const formData = new FormData();
		formData.append('action', 'load_projects_by_category');
		formData.append('category_id', categoryId);
		formData.append('nonce', atlasData.nonce || '');

		fetch(atlasData.ajaxUrl, {
			method: 'POST',
			body: formData,
		})
			.then((response) => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.json();
			})
			.then((data) => {
				gridElement.dataset.loading = 'false';

			if (data.success && data.data && data.data.html) {
				gridElement.innerHTML = data.data.html;

				// Setup observers for new items
				const newItems = gridElement.querySelectorAll('.block-projects-preview__item');
				if (newItems.length > 0) {
					self.setupItemObservers(newItems);
				}
			} else {
				gridElement.innerHTML = '<p>No projects found</p>';
			}
			})
			.catch((error) => {
				console.error('ProjectsPreview AJAX Error:', error);
				gridElement.innerHTML = '<p>Error loading projects</p>';
				gridElement.dataset.loading = 'false';
			});
	},

	/**
	 * Initialize scroll animations using single IntersectionObserver
	 */
	initScrollAnimations: function () {
		const blockWrapper = document.querySelector('.block-projects-preview');
		if (!blockWrapper) return;

		// Observe nav tabs for slideInRight animation
		const navTabs = blockWrapper.querySelector('.block-projects-preview__tabs');
		if (navTabs) {
			this.observeElement(navTabs, 'animate__slideInRight', 0);
		}

		// Observe grid items for staggered animation
		const items = blockWrapper.querySelectorAll('.block-projects-preview__item');
		if (items.length > 0) {
			this.setupItemObservers(items);
		}
	},

	/**
	 * Animate items immediately (for tab switching)
	 */
	animateItems: function (items) {
		if (!items || items.length === 0) return;

		items.forEach((item, index) => {
			// Remove existing animation classes first
			item.classList.remove('animate__animated', 'animate__slideInUp');
			item.style.animationDelay = '';

			// Re-apply animation in the next paint cycle without forcing sync layout
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					const delay = index * 100;
					item.style.animationDelay = delay + 'ms';
					item.classList.add('animate__animated', 'animate__slideInUp');
				});
			});
		});
	},

	/**
	 * Setup observers for items with single observer instance
	 */
	setupItemObservers: function (items) {
		if (!('IntersectionObserver' in window) || items.length === 0) return;

		const self = this;

		// Clean up old observer if exists
		if (this.activeObserver) {
			this.activeObserver.disconnect();
		}

		// Create single observer for all items
		this.activeObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					// Calculate delay based on element's current position
					const allItems = entry.target.parentElement.querySelectorAll('.block-projects-preview__item');
					const itemIndex = Array.from(allItems).indexOf(entry.target);
					const delay = Math.max(0, itemIndex * 100);

					entry.target.style.animationDelay = delay + 'ms';
					entry.target.classList.add('animate__animated', 'animate__slideInUp');
					
					// Unobserve after animation starts
					self.activeObserver.unobserve(entry.target);
				}
			});
		}, this.observerOptions);

		// Observe all items
		items.forEach((item) => {
			this.activeObserver.observe(item);
		});
	},

	/**
	 * Observe element for scroll into view
	 */
	observeElement: function (element, animationClass, delay) {
		if (!element || !('IntersectionObserver' in window)) return;

		// Skip if already animated
		if (element.classList.contains('animate__animated')) return;

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !entry.target.classList.contains('animate__animated')) {
					if (delay > 0) {
						entry.target.style.animationDelay = delay + 'ms';
					}

					entry.target.classList.add('animate__animated', animationClass);
					observer.unobserve(entry.target); // Clean up after animation
				}
			});
		}, this.observerOptions);

		observer.observe(element);
	},

	/**
	 * Cleanup on page unload
	 */
	destroy: function () {
		if (this.activeObserver) {
			this.activeObserver.disconnect();
			this.activeObserver = null;
		}

		if (this.tabClickTimeout) {
			clearTimeout(this.tabClickTimeout);
			this.tabClickTimeout = null;
		}

		this.initialized = false;
	},
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		ProjectsPreview.init();
	});
} else {
	ProjectsPreview.init();
}

// Cleanup on navigation/unload
window.addEventListener('beforeunload', () => {
	ProjectsPreview.destroy();
});
